import { ExternalLink, ImagePlus } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ScrollReveal } from "@/components/motion/ScrollReveal";
import { cn } from "@/lib/utils";
import {
  filledSponsorTiers,
  sponsorTiers,
  type LogoBleed,
  type Sponsor,
  type SponsorTier,
  type SponsorTierId
} from "./data";

/*
 * Sponsor logo wall.
 *
 * Tier tone is carried by a DECORATIVE BAR ONLY — never by the heading text.
 * Measured against the white tile, the celebration accents fail AA badly
 * (accent-orange ~2.3:1, accent-teal ~2.4:1, accent ~1.9:1). PackagesGrid gets
 * away with tinted text because its tone sits on a tiny eyebrow beside a black
 * card title; here the tier name IS the content, so it stays text-foreground.
 *
 * Tailwind needs literal class names, so every tone/size string is spelled out
 * rather than templated (same convention as SponsorshipPage / PackagesGrid).
 */
const TIER_TONES: Record<SponsorTierId, { bar: string }> = {
  // The event's owners → Bursa navy, the theme's secondary and what
  // SponsorshipPage's INKIND_TONE already uses for "outside the tier palette".
  organisers: { bar: "bg-secondary" },
  // Naming right, not a rung on the paid ladder → brand red, which is what the
  // codebase already uses for "outside the tier palette".
  officialBank: { bar: "bg-primary" },
  // The four below mirror PackagesGrid's TIER_TONES exactly so the two pages agree.
  platinum: { bar: "bg-accent" },
  gold: { bar: "bg-accent-magenta" },
  silver: { bar: "bg-accent-teal" },
  bronze: { bar: "bg-accent-orange" },
  supporting: { bar: "bg-accent-violet" }
};

/*
 * Optical size cap. The tile is a fixed box and the logo is object-contain, so
 * whichever dimension binds first wins — but the source files carry very
 * different baked-in whitespace, so an identical cap renders the padded ones
 * visibly smaller. This corrects for that without editing any artwork.
 */
const LOGO_BLEED: Record<LogoBleed, string> = {
  full: "max-h-[72%]",
  normal: "max-h-[85%]",
  padded: "max-h-full"
};

/*
 * Show the unsold ladder tiers as dashed "slot available" tiles linking to
 * /sponsorship. OFF by default — an empty Platinum row on a credibility page
 * reads as a vacancy, not an invitation. Flip to true if the client wants the
 * open tiers advertised.
 */
const SHOW_EMPTY_TIER_SLOTS = false;

type TileSize = "standard" | "compact";

/**
 * Outer width — drives how many logos land per row at each breakpoint.
 *
 * Every tier renders at `standard`: the wall states who backs the event, and
 * sizing one logo larger than another is a claim about relative billing that
 * the tier headings already make in words.
 */
export const TILE_WIDTH: Record<TileSize, string> = {
  standard: "flex w-[9.5rem] sm:w-[11rem] md:w-[12rem] lg:w-[13rem]",
  /*
   * The footer strip carries its groups on every page, so tiles run tighter
   * there. That row is static and finite: it centres while it fits and turns
   * into a plain horizontal scroller once it doesn't, so this width sets how
   * many sponsors land on screen before anyone has to scroll — currently all
   * four on desktop, and it scrolls on phones.
   */
  compact: "flex w-[7rem] sm:w-[7.5rem] md:w-[8rem]"
};

/** Inner box height + padding. */
const TILE_BOX: Record<TileSize, string> = {
  standard: "h-24 px-5 py-4 md:h-28 md:px-6 md:py-5",
  compact: "h-16 px-4 py-3 md:h-20 md:px-5"
};

/*
 * bg-card is pure white and is MANDATORY here: the UOB artwork carries a hard
 * white matte, which would read as a white rectangle floating on the page's
 * pale-blue background. A white tile makes the matted logo and the transparent
 * PNGs sit identically. Same reasoning as the white logo panel in Footer.tsx.
 *
 * rounded-xl + border + shadow-sm is the shadcn Card recipe, hand-written
 * because this must be an <a>, not a <div> — exactly what PathCard in
 * SponsorshipPage.tsx does for the same reason.
 */
const TILE_BASE =
  "group relative flex w-full items-center justify-center rounded-xl border bg-card shadow-sm " +
  "transition-[box-shadow,border-color,transform] duration-200 " +
  "hover:border-primary/30 hover:shadow-md motion-safe:hover:-translate-y-0.5 " +
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring " +
  "focus-visible:ring-offset-2 focus-visible:ring-offset-background";

export function SponsorTile({
  sponsor,
  size = "standard",
  eager = false
}: {
  sponsor: Sponsor;
  size?: TileSize;
  eager?: boolean;
}) {
  const { t } = useTranslation("sponsors");

  const logo = (
    <img
      src={sponsor.logo}
      alt={sponsor.name}
      width={sponsor.width}
      height={sponsor.height}
      loading={eager ? "eager" : "lazy"}
      decoding="async"
      /*
       * Inline, not an aspect-[w/h] class: the ratio comes from data and
       * Tailwind arbitrary values can't be templated. Required, not cosmetic —
       * Header.tsx documents the Safari flexbox <img> bug where a wide image
       * renders at near-natural size and blows up the layout, and these tiles
       * are flex containers with wide <img> children.
       */
      style={{ aspectRatio: `${sponsor.width} / ${sponsor.height}` }}
      className={cn("w-auto max-w-full object-contain", LOGO_BLEED[sponsor.bleed ?? "normal"])}
    />
  );

  return (
    <a
      href={sponsor.href}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(TILE_BASE, TILE_BOX[size])}
    >
      {logo}
      {/* The alt text is the link's accessible name; this appends the new-tab
          warning to it (WCAG G201). No aria-label on the <a> — it would
          override the alt and leave the image unnamed. */}
      <span className="sr-only"> {t("logo.opensInNewTab")}</span>
      {/* Always visible, not hover-only: a hover-only affordance is invisible
          to keyboard and touch users. */}
      {size !== "compact" && (
        <ExternalLink
          aria-hidden
          className="pointer-events-none absolute bottom-2 right-2 h-3.5 w-3.5 text-muted-foreground/40 transition-colors duration-200 group-hover:text-primary"
        />
      )}
    </a>
  );
}

function TierHeading({ tier }: { tier: SponsorTier }) {
  const { t } = useTranslation("sponsors");
  return (
    <ScrollReveal className="flex flex-col items-center text-center">
      <span aria-hidden className={cn("h-1 w-12 rounded-full", TIER_TONES[tier.id].bar)} />
      <h2 className="mt-4 text-2xl md:text-3xl">{t(`tiers.${tier.id}`)}</h2>
    </ScrollReveal>
  );
}

function EmptySlot() {
  const { t } = useTranslation("sponsors");
  return (
    <Link
      to="/sponsorship"
      className="grid h-24 w-full place-items-center rounded-xl border-2 border-dashed border-border bg-muted/40 px-5 py-4 text-center transition-colors duration-200 hover:border-primary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 md:h-28"
    >
      <span>
        <ImagePlus aria-hidden className="mx-auto h-6 w-6 text-foreground/40" />
        <span className="mt-2 block text-sm text-muted-foreground">{t("emptySlot.title")}</span>
      </span>
    </Link>
  );
}

export function SponsorWall() {
  const { t } = useTranslation("sponsors");
  const tiers = SHOW_EMPTY_TIER_SLOTS ? sponsorTiers : filledSponsorTiers;

  // Fresh year, nothing announced yet — say so rather than render a hero, a
  // hole, then a CTA.
  if (tiers.length === 0) {
    return (
      <section className="container py-16 md:py-20">
        <ScrollReveal className="mx-auto max-w-xl text-center">
          <h2 className="text-2xl md:text-3xl">{t("pending.title")}</h2>
          <p className="mt-3 text-muted-foreground">{t("pending.body")}</p>
        </ScrollReveal>
      </section>
    );
  }

  return (
    <section className="container py-16 md:py-20">
      {tiers.map((tier, index) => {
        const tierLabel = t(`tiers.${tier.id}`);

        return (
          <div
            key={tier.id}
            /* A rule between tiers, mirroring the reference wall. Not the
               alternating bg-muted/30 band idiom — with up to six tiers that
               zebra-stripes the whole page. */
            className={
              index === 0 ? undefined : "mt-14 border-t border-border pt-14 md:mt-16 md:pt-16"
            }
          >
            <TierHeading tier={tier} />

            {/* Centred flex-wrap rather than a grid: a tier may hold one logo
                or eight, and a grid leaves a lone logo hard-left and a partial
                row with a hole. Wrapping centres every row at any count. */}
            <ScrollReveal.Group
              role="list"
              aria-label={tierLabel}
              className="mt-8 flex flex-wrap items-stretch justify-center gap-4 md:gap-6"
              stagger={0.07}
            >
              {tier.sponsors.map((sponsor) => (
                <ScrollReveal.Item
                  key={sponsor.id}
                  role="listitem"
                  className={TILE_WIDTH.standard}
                >
                  <SponsorTile sponsor={sponsor} eager={index === 0} />
                </ScrollReveal.Item>
              ))}

              {SHOW_EMPTY_TIER_SLOTS && tier.sponsors.length === 0 && (
                <ScrollReveal.Item role="listitem" className={TILE_WIDTH.standard}>
                  <EmptySlot />
                </ScrollReveal.Item>
              )}
            </ScrollReveal.Group>
          </div>
        );
      })}
    </section>
  );
}
